import { useDispatch } from "react-redux";
import {
  invalidateAssignmentCache,
  invalidateGroupAssignmentCache,
} from "store/actions/assignments";
import { invalidateSubmissionsCache } from "store/actions/submissions";
import useFetchAssignments from "./assignments/useFetchAssignments";
import useFetchSubmissions from "./submissions/useFetchSubmissions";

/**
 * Hook to provide cache invalidation functionality
 * Use this when you need to force refresh cached data after updates
 */
const useCacheInvalidation = () => {
  const dispatch = useDispatch();
  const { fetchAllAssignments, fetchGroupAssignments } = useFetchAssignments();
  const {
    fetchAllSubmissions,
    fetchGroupSubmissions,
    fetchPracticeSubmissions,
  } = useFetchSubmissions();

  /**
   * Invalidates all caches and forces refresh of all data
   * Use this after template updates that affect assignments
   */
  const invalidateAllCaches = async () => {
    // Dispatch cache invalidation actions
    dispatch(invalidateAssignmentCache());
    dispatch(invalidateGroupAssignmentCache());
    dispatch(invalidateSubmissionsCache());

    // Force refresh all cached data
    await Promise.all([
      fetchAllAssignments(),
      fetchGroupAssignments(undefined),
      fetchAllSubmissions(false, true),
      fetchGroupSubmissions(undefined, true),
      fetchPracticeSubmissions(true),
    ]);
  };

  /**
   * Invalidates only assignment caches
   * Use this when only assignment data needs refreshing
   */
  const invalidateAssignmentCaches = async () => {
    dispatch(invalidateAssignmentCache());
    dispatch(invalidateGroupAssignmentCache());

    await Promise.all([fetchAllAssignments(), fetchGroupAssignments(null)]);
  };

  /**
   * Invalidates only submission caches
   * Use this when only submission data needs refreshing
   */
  const invalidateSubmissionCaches = async () => {
    dispatch(invalidateSubmissionsCache());

    await Promise.all([
      fetchAllSubmissions(false, true),
      fetchGroupSubmissions(undefined, true),
      fetchPracticeSubmissions(true),
    ]);
  };

  return {
    invalidateAllCaches,
    invalidateAssignmentCaches,
    invalidateSubmissionCaches,
  };
};

export default useCacheInvalidation;
